import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { client } from "../../api/client";
import { globalConstants } from "../../constants/global.constants";

const initialState = {
  status: globalConstants.IDLE,
  notifications: [],
  recentNotifications: [],
  unreadCount: 0,
  pagination: {
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0,
  },
  error: null,
};

// Get all notifications for the user
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async ({ perPage = 20, unreadOnly = false, search, sender, type, dateFrom, dateTo } = {}, thunkAPI) => {
    try {
      const response = await client.post("notifications", { 
        per_page: perPage,
        unread_only: unreadOnly,
        search,
        sender,
        type,
        date_from: dateFrom,
        date_to: dateTo
      });
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// Get unread notification count
export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (_, thunkAPI) => {
    try {
      const response = await client.post("notifications/unread_count");
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// Get recent notifications (for popup)
export const fetchRecentNotifications = createAsyncThunk(
  "notifications/fetchRecentNotifications",
  async ({ limit = 5, sinceMinutes = 30 } = {}, thunkAPI) => {
    try {
      const response = await client.post("notifications/recent", {
        limit,
        since_minutes: sinceMinutes
      });
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// Mark notification as read
export const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (notificationId, thunkAPI) => {
    try {
      const response = await client.post("notifications/mark_as_read", {
        notification_id: notificationId
      });
      return { notificationId, ...response };
    } catch (error) {
      throw error;
    }
  }
);

// Mark all notifications as read
export const markAllAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_, thunkAPI) => {
    try {
      const response = await client.post("notifications/mark_all_as_read");
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// Delete notification
export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async (notificationId, thunkAPI) => {
    try {
      const response = await client.post("notifications/delete", {
        notification_id: notificationId
      });
      return { notificationId, ...response };
    } catch (error) {
      throw error;
    }
  }
);

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearNotifications: (state) => {
      state.notifications = [];
      state.recentNotifications = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.status = globalConstants.LOADING;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = globalConstants.SUCCEEDED;
        state.notifications = action.payload.notifications || [];
        state.pagination = action.payload.pagination || state.pagination;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = globalConstants.ERROR;
        state.error = action.error.message;
      })
      
      // Fetch unread count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unread_count || 0;
      })
      
      // Fetch recent notifications
      .addCase(fetchRecentNotifications.pending, (state) => {
        // Don't change status for background fetch
      })
      .addCase(fetchRecentNotifications.fulfilled, (state, action) => {
        state.recentNotifications = action.payload.notifications || [];
      })
      
      // Mark as read
      .addCase(markAsRead.fulfilled, (state, action) => {
        const notificationId = action.payload.notificationId;
        
        // Update in notifications array
        const notification = state.notifications.find(n => n.id === notificationId);
        if (notification) {
          notification.is_read = true;
          notification.read_at = new Date().toISOString();
        }
        
        // Update in recent notifications
        const recentNotification = state.recentNotifications.find(n => n.id === notificationId);
        if (recentNotification) {
          recentNotification.is_read = true;
          recentNotification.read_at = new Date().toISOString();
        }
        
        // Decrease unread count
        if (state.unreadCount > 0) {
          state.unreadCount -= 1;
        }
      })
      
      // Mark all as read
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications = state.notifications.map(n => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString()
        }));
        state.recentNotifications = state.recentNotifications.map(n => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString()
        }));
        state.unreadCount = 0;
      })
      
      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const notificationId = action.payload.notificationId;
        
        // Remove from notifications array
        state.notifications = state.notifications.filter(n => n.id !== notificationId);
        
        // Remove from recent notifications
        state.recentNotifications = state.recentNotifications.filter(n => n.id !== notificationId);
        
        // Decrease unread count if it was unread
        const wasUnread = state.notifications.find(n => n.id === notificationId && !n.is_read);
        if (wasUnread && state.unreadCount > 0) {
          state.unreadCount -= 1;
        }
      });
  },
});

export const { clearNotifications } = notificationsSlice.actions;

export default notificationsSlice.reducer;

